import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';

export type WidgetStatus = 'loading' | 'success' | 'error' | 'no-data';

interface WidgetStatusEntry {
  status: WidgetStatus;
  timestamp: number;
}

interface DashboardWidgetContextType {
  /** Register a widget's status */
  reportWidgetStatus: (widgetId: string, status: WidgetStatus) => void;
  /** Get the current status of all widgets */
  getWidgetStatuses: () => Map<string, WidgetStatusEntry>;
  /** Check if auto-refresh should be triggered */
  shouldAutoRefresh: () => boolean;
  /** Reset all widget statuses */
  resetStatuses: () => void;
  /** Flag indicating if auto-refresh has been triggered */
  autoRefreshTriggered: boolean;
  /** Set the auto-refresh triggered flag */
  setAutoRefreshTriggered: (value: boolean) => void;
}

const DashboardWidgetContext = createContext<DashboardWidgetContextType | null>(null);

// Threshold: if more than this percentage of widgets have errors/no-data, trigger refresh
const ERROR_THRESHOLD_PERCENTAGE = 0.5; // 50%
// Minimum number of widgets before we consider auto-refresh
const MIN_WIDGETS_FOR_AUTO_REFRESH = 3;
// Debounce time to wait for all widgets to report their status
const STATUS_CHECK_DELAY_MS = 3000;

export const DashboardWidgetProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const widgetStatuses = useRef<Map<string, WidgetStatusEntry>>(new Map());
  const [autoRefreshTriggered, setAutoRefreshTriggered] = useState(false);
  const checkTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const reportWidgetStatus = useCallback((widgetId: string, status: WidgetStatus) => {
    widgetStatuses.current.set(widgetId, {
      status,
      timestamp: Date.now(),
    });
    
    console.log(`[DashboardWidgetContext] Widget "${widgetId}" reported status: ${status}`);
    
    // Debounce the check to allow all widgets to report
    if (checkTimeoutRef.current) {
      clearTimeout(checkTimeoutRef.current);
    }
    
    checkTimeoutRef.current = setTimeout(() => {
      const shouldRefresh = shouldAutoRefresh();
      if (shouldRefresh && !autoRefreshTriggered) {
        console.log('[DashboardWidgetContext] Auto-refresh threshold reached, triggering refresh...');
        setAutoRefreshTriggered(true);
      }
    }, STATUS_CHECK_DELAY_MS);
  }, [autoRefreshTriggered]);

  const getWidgetStatuses = useCallback(() => {
    return new Map(widgetStatuses.current);
  }, []);

  const shouldAutoRefresh = useCallback(() => {
    const statuses = widgetStatuses.current;
    const totalWidgets = statuses.size;
    
    // Don't trigger if we don't have enough widgets reporting
    if (totalWidgets < MIN_WIDGETS_FOR_AUTO_REFRESH) {
      console.log(`[DashboardWidgetContext] Not enough widgets (${totalWidgets}/${MIN_WIDGETS_FOR_AUTO_REFRESH})`);
      return false;
    }
    
    // Count widgets with errors or no-data
    let errorCount = 0;
    let loadingCount = 0;
    
    statuses.forEach((entry) => {
      if (entry.status === 'error' || entry.status === 'no-data') {
        errorCount++;
      } else if (entry.status === 'loading') {
        loadingCount++;
      }
    });
    
    // If widgets are still loading, don't trigger yet
    if (loadingCount > 0) {
      console.log(`[DashboardWidgetContext] ${loadingCount} widgets still loading, waiting...`);
      return false;
    }
    
    const errorPercentage = errorCount / totalWidgets;
    console.log(`[DashboardWidgetContext] Error rate: ${errorCount}/${totalWidgets} (${(errorPercentage * 100).toFixed(1)}%)`);
    
    return errorPercentage >= ERROR_THRESHOLD_PERCENTAGE;
  }, []);

  const resetStatuses = useCallback(() => {
    widgetStatuses.current.clear();
    setAutoRefreshTriggered(false);
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (checkTimeoutRef.current) {
        clearTimeout(checkTimeoutRef.current);
      }
    };
  }, []);

  return (
    <DashboardWidgetContext.Provider
      value={{
        reportWidgetStatus,
        getWidgetStatuses,
        shouldAutoRefresh,
        resetStatuses,
        autoRefreshTriggered,
        setAutoRefreshTriggered,
      }}
    >
      {children}
    </DashboardWidgetContext.Provider>
  );
};

export const useDashboardWidgetContext = () => {
  const context = useContext(DashboardWidgetContext);
  if (!context) {
    // Return a no-op implementation if used outside provider
    return {
      reportWidgetStatus: () => {},
      getWidgetStatuses: () => new Map(),
      shouldAutoRefresh: () => false,
      resetStatuses: () => {},
      autoRefreshTriggered: false,
      setAutoRefreshTriggered: () => {},
    };
  }
  return context;
};

/**
 * Hook to easily report widget status based on loading/error/data states
 * @param widgetId Unique identifier for the widget
 * @param isLoading Whether the widget is loading
 * @param error Error state (truthy = error)
 * @param hasData Whether the widget has data to display
 */
export const useWidgetStatusReporter = (
  widgetId: string,
  isLoading: boolean,
  error: any,
  hasData: boolean
) => {
  const { reportWidgetStatus } = useDashboardWidgetContext();

  React.useEffect(() => {
    if (isLoading) {
      reportWidgetStatus(widgetId, 'loading');
    } else if (error && !hasData) {
      reportWidgetStatus(widgetId, 'error');
    } else if (!hasData) {
      reportWidgetStatus(widgetId, 'no-data');
    } else {
      reportWidgetStatus(widgetId, 'success');
    }
  }, [widgetId, isLoading, error, hasData, reportWidgetStatus]);
};

export default DashboardWidgetContext;
